import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Star as StarIcon,
  ExitToApp as LeaveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import '../components/team/utils/compatibility-fix';

import useTeamOverview from '../hooks/useTeamOverview';
import useTeamMembers from '../hooks/useTeamMembers';
import useTeamRoles from '../hooks/useTeamRoles';
import useTeamInvitations from '../hooks/useTeamInvitations';
import useTeamSubscription from '../hooks/useTeamSubscription';
import { Team as AppTeam, TabsConfig } from '../types';
import { useOrganization, Team, Invitation } from '../context/OrganizationContext';
import { useTeamPermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { Subscription } from '../types';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../services';
import { formatDistanceToNow } from 'date-fns';
import { formatDate } from '../utils/date-formatter';
import { bytesToMB } from '../utils/storage-formatter';
import { isLastAdminError } from '../utils/admin-validation-error-handler';
import organizationApi from '../services/api/organization.api';
import ConfirmDialog from '../components/common/dialogs/ConfirmDialog';

import TeamHeader from '../components/team/TeamHeader';
import TeamSummary from '../components/team/TeamSummary';
import TeamMembersList from '../components/team/TeamMembersList';
import BulkInviteDialog from '../components/organization/BulkInviteDialog';
import TeamSettings from '../components/team/TeamSettings';
import TeamSubscriptionDialog from '../components/team/TeamSubscriptionDialog';
import TabPanel from '../components/common/TabPanel';
import RoleManager from '../components/rbac/RoleManager';

const TeamDetail: React.FC = () => {
  const { orgId, teamId } = useParams<{ orgId: string; teamId: string }>();
  const navigate = useNavigate();
  const { organizations, getTeamSubscription, getOrganizationSubscriptions, assignSubscriptionToTeam, refreshData, refreshMemberships } = useOrganization();
  
  // Updated hooks using new endpoints
  const {
    team,
    organization,
    userRole,
    permissions,
    roles: teamRoles,
    isLoading: teamLoading,
    error: teamError,
    updateTeam,
    deleteTeam,
    refreshOverview
  } = useTeamOverview(teamId);
  
  // Get the organization from the context based on orgId
  const currentOrganization = organizations.find(org => org.id === orgId) || organization;

  // Check permissions
  const { canDeleteTeam, hasPermission } = useTeamPermissions(teamId);
  const canInviteMembers = hasPermission(PERMISSIONS.MEMBER_INVITE);
  const canCancelInvitations = hasPermission(PERMISSIONS.INVITATION_CANCEL);
  const canManageSubscription = hasPermission(PERMISSIONS.SUBSCRIPTION_ASSIGN_TEAM);
  const canCreateContent = hasPermission(PERMISSIONS.CONTENT_CREATE);

  const {
    members,
    isLoading: membersLoading,
    updateMemberRole,
    removeMember
  } = useTeamMembers(teamId || '', orgId);
  
  const {
    roles,
    isLoading: rolesLoading,
    refreshRoles
  } = useTeamRoles(teamId || '');
  
  const {
    invitations,
    isLoading: invitationsLoading,
    createInvitation,
    refreshInvitations
  } = useTeamInvitations(teamId || '', orgId);
  
  const {
    subscriptionData,
    subscription: teamSubscriptionNew,
    usage,
    isLoading: subscriptionLoading,
    refreshSubscription
  } = useTeamSubscription(teamId || '', orgId);

  // Local state
  const [tabIndex, setTabIndex] = useState(0);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuInvitation, setMenuInvitation] = useState<Invitation | null>(null);
  const [changeSubscriptionDialogOpen, setChangeSubscriptionDialogOpen] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const [leaveTeamDialogOpen, setLeaveTeamDialogOpen] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  
  // Use new subscription data structure
  const teamSubscription = teamSubscriptionNew || (teamId ? getTeamSubscription(teamId) : null);
  const [availableSubscriptions, setAvailableSubscriptions] = useState(orgId ? getOrganizationSubscriptions(orgId) : []);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(teamSubscription?._id || '');
  
  useEffect(() => {
    // Update subscription data when IDs change
    if (teamSubscription) {
      setSelectedSubscriptionId(teamSubscription._id || '');
    }
    
    if (orgId) {
      const orgSubscriptions = getOrganizationSubscriptions(orgId);
      setAvailableSubscriptions(orgSubscriptions);
    }
  }, [orgId, teamSubscription, getOrganizationSubscriptions]);

  // Parse tab from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const tabValue = parseInt(hash.replace('#', ''));
      if (!isNaN(tabValue) && tabValue >= 0 && tabValue <= 4) {
        setTabIndex(tabValue);
      }
    }
  }, []);

  // Tab change handler
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    window.location.hash = `${newValue}`;
  };

  // Invitation handlers
  const handleInviteDialogOpen = () => setInviteDialogOpen(true);
  const handleInviteDialogClose = () => setInviteDialogOpen(false);
  const handleBulkInviteComplete = async (results: { successful_count: number; failed_count: number }) => {
    const { successful_count, failed_count } = results;
    if (successful_count > 0) {
      setNotification({
        message: failed_count > 0
          ? `${successful_count} invitation${successful_count !== 1 ? 's' : ''} sent, ${failed_count} failed`
          : `${successful_count} invitation${successful_count !== 1 ? 's' : ''} sent successfully`,
        severity: failed_count > 0 ? 'warning' : 'success'
      });
      await refreshInvitations();
    } else if (failed_count > 0) {
      setNotification({ message: 'All invitations failed to send', severity: 'error' });
    }
  };

  // Grid helper functions
  const getStatusText = (status: string | number) => {
    const statusNum = typeof status === 'string' ? parseInt(status) : status;
    switch (statusNum) {
      case 0: return 'Pending';
      case 1: return 'Accepted';
      case 2: return 'Declined';
      case 3: return 'Expired';
      case 4: return 'Cancelled';
      default: 
        // Try string values as fallback
        if (typeof status === 'string') {
          return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        }
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string | number) => {
    const statusNum = typeof status === 'string' ? parseInt(status) : status;
    switch (statusNum) {
      case 0: return 'warning';  // Pending
      case 1: return 'success';  // Accepted
      case 2: return 'error';    // Declined  
      case 3: return 'default';  // Expired
      case 4: return 'default';  // Cancelled
      default: return 'default';
    }
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => {
    setMenuAnchor(event.currentTarget);
    setMenuInvitation(invitation);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuInvitation(null);
  };

  const handleLeaveTeam = async () => {
    if (!teamId) return;

    setIsLeavingTeam(true);
    try {
      await organizationApi.leaveTeam(teamId);
      // Refresh the selector's org/team caches so the left team drops out.
      await refreshMemberships();
      setNotification({
        message: 'Successfully left team',
        severity: 'success'
      });
      setLeaveTeamDialogOpen(false);
      // Navigate back to organization or dashboard
      navigate(orgId ? `/organizations/${orgId}` : '/dashboard');
    } catch (error: any) {
      console.error('Error leaving team:', error);
      setLeaveTeamDialogOpen(false);

      // Check if this is a last admin error
      if (isLastAdminError(error)) {
        setNotification({
          message: 'Cannot leave team: You are the last administrator. Please add another admin before leaving.',
          severity: 'error'
        });
      } else {
        setNotification({
          message: error?.message || 'Failed to leave team',
          severity: 'error'
        });
      }
    } finally {
      setIsLeavingTeam(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (menuInvitation) {
      try {
        await invitationService.cancelInvitation(menuInvitation.id);
        setNotification({
          message: `Invitation cancelled successfully`,
          severity: 'success'
        });
        await refreshInvitations();
      } catch (error) {
        console.error('Error canceling invitation:', error);
        setNotification({
          message: 'Failed to cancel invitation',
          severity: 'error'
        });
      }
    }
    handleMenuClose();
  };

  // Transform raw invitation data to match Invitation interface
  const transformedInvitations = (invitations || []).map((invitation: any) => ({
    id: invitation.id,
    _id: invitation.id, // Add _id as alias
    inviteeEmail: invitation.inviteeEmail,
    email: invitation.inviteeEmail, // Alias for backward compatibility
    userId: invitation.inviterUserId,
    scope: 'team' as const, // Team invitations are always team-scoped
    contextId: teamId,
    organizationId: invitation.organizationId,
    organizationName: organization?.name,
    teamName: invitation.teamName,
    roleId: invitation.roleId,
    roleName: invitation.roleName,
    invitedBy: invitation.inviterUserId,
    inviterName: invitation.inviterName,
    status: invitation.status,
    expiresAt: invitation.expiresAt,
    acceptedAt: invitation.acceptedAt,
    token: invitation.token || '',
    createdAt: invitation.createdAt,
    updatedAt: invitation.updatedAt
  }));

  // DataGrid columns definition
  const invitationColumns: GridColDef[] = [
    {
      field: 'inviteeEmail',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'roleName',
      headerName: 'Role',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.value || 'Not specified'
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const statusValue = params.value ?? params.row.status;
        const statusText = getStatusText(statusValue);
        return (
          <Chip 
            label={statusText} 
            color={getStatusColor(statusValue) as any}
            size="small"
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Sent',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value;
        return date ? formatDate(date) : '-';
      },
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const invitation = params.row;
        // Only show expiration for pending invitations
        const statusNum = typeof invitation.status === 'string' ? parseInt(invitation.status) : invitation.status;
        const isPending = statusNum === 0;

        if (!isPending) {
          return '-';
        }

        const date = params.value;
        if (!date) return 'No expiration';
        try {
          return formatDistanceToNow(new Date(date), { addSuffix: true });
        } catch (error) {
          return 'Invalid date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        const invitation = params.row;

        // Check if user has permission to cancel invitations
        if (!canCancelInvitations) {
          return null;
        }

        // Check if invitation can be cancelled — only PENDING invitations.
        // Accepted/declined/expired/cancelled are terminal and not cancellable.
        const statusNum = typeof invitation.status === 'string' ? parseInt(invitation.status) : invitation.status;
        const canCancel = invitation.status === 'pending' || statusNum === 0;

        if (!canCancel) {
          return null;
        }

        return (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, invitation)}
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];

  // Subscription handlers
  const handleSubscriptionDialogOpen = () => setChangeSubscriptionDialogOpen(true);
  const handleSubscriptionDialogClose = () => {
    setChangeSubscriptionDialogOpen(false);
    setSelectedSubscriptionId(teamSubscription?._id || '');
  };
  const handleChangeSubscription = async (subscriptionId: string) => {
    if (teamId && subscriptionId) {
      try {
        await assignSubscriptionToTeam(subscriptionId, teamId);

        // Show success notification
        setNotification({
          message: 'Subscription assigned successfully',
          severity: 'success'
        });

        // Close dialog immediately
        handleSubscriptionDialogClose();

        // Refresh data in background to update the UI
        console.log('Refreshing team subscription data after assignment...');
        Promise.all([
          refreshSubscription(),   // Refresh team's subscription details
          refreshOverview()        // Refresh team overview (includes subscription info)
        ]).then(() => {
          console.log('Team subscription data refreshed successfully');
        }).catch(err => {
          console.error('Failed to refresh team data:', err);
        });
      } catch (error: any) {
        console.error('Error changing subscription:', error);

        // Show error notification to user
        const errorMessage = error?.response?.data?.error?.message ||
                           error?.message ||
                           'Failed to assign subscription';
        setNotification({
          message: errorMessage,
          severity: 'error'
        });

        // Keep dialog open so user can try again or cancel
      }
    }
  };

  // Loading state
  const isLoading = teamLoading || membersLoading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (teamError || !team) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Team Not Found
          </Typography>
          <Button 
            variant="contained" 
            component={Link} 
            to={orgId ? `/organizations/${orgId}` : '/dashboard'}
          >
            {orgId ? 'Return to Organization' : 'Return to Dashboard'}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Tab configuration with enhanced functionality
  const tabsConfig: TabsConfig = [
    { label: 'Overview', id: 'team-tab-0', ariaControls: 'team-tabpanel-0' },
    { label: 'Members', id: 'team-tab-1', ariaControls: 'team-tabpanel-1' },
    { label: 'Invitations', id: 'team-tab-2', ariaControls: 'team-tabpanel-2' },
    { label: 'Roles', id: 'team-tab-3', ariaControls: 'team-tabpanel-3' },
    { label: 'Settings', id: 'team-tab-4', ariaControls: 'team-tabpanel-4' }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Team header */}
      <TeamHeader 
        team={team}
        organization={currentOrganization}
        onUpdate={updateTeam}
      />

      {/* Subscription status and usage - Only show to users with permission */}
      {canManageSubscription && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {teamSubscription ? (
              <Chip
                icon={<MonetizationOnIcon />}
                label={`${teamSubscription.name || 'Active Subscription'}`}
                variant="outlined"
                color="primary"
                onClick={handleSubscriptionDialogOpen}
                clickable
              />
            ) : (
              <Chip
                icon={<MonetizationOnIcon />}
                label="No Subscription Assigned"
                variant="outlined"
                color="error"
                onClick={handleSubscriptionDialogOpen}
                clickable
              />
            )}
          </Box>
        </Box>
      )}

      {/* Tabs navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabIndex} 
          onChange={handleTabChange} 
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {tabsConfig.map((tab, index) => (
            <Tab 
              key={tab.id}
              label={tab.label} 
              id={tab.id} 
              aria-controls={tab.ariaControls} 
            />
          ))}
        </Tabs>
      </Paper>

      {/* Overview tab */}
      <TabPanel value={tabIndex} index={0}>
        {team && (
          <Box>
            <TeamSummary
              team={{ ...team, id: team._id || (team as any).id || '' } as any}
              memberCount={members.length}
              onInviteMember={handleInviteDialogOpen}
              onManageSubscription={handleSubscriptionDialogOpen}
              onCreateContent={() => navigate('/content/upload')}
              subscription={teamSubscription}
              usage={usage}
              isLoading={subscriptionLoading}
              canInviteMembers={canInviteMembers}
              canManageSubscription={canManageSubscription}
              canCreateContent={canCreateContent}
            />

            {/* Enhanced team overview information */}
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid xs={12} md={8}>
                <Card
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    boxShadow: 'none',
                    '&:hover': { transform: 'none', boxShadow: 'none' }
                  }}
                >
                  <CardHeader
                    avatar={<InfoIcon color="info" />}
                    title="Team Information"
                    subheader="Team statistics and details"
                  />
                  <Divider />
                  <CardContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1">Members</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {members?.length || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1">Roles Available</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {teamRoles?.length || roles?.length || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1">Pending Invitations</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {invitations?.filter((inv: any) => inv.status === 0 || inv.status === 'pending').length || 0}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Organization</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {organization?.name || (team as any).organizationName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Created</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(team as any).created_at ? new Date((team as any).created_at).toLocaleDateString() : (team.createdAt ? new Date(team.createdAt).toLocaleDateString() : 'Unknown')}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </TabPanel>

      {/* Members tab */}
      <TabPanel value={tabIndex} index={1}>
        <TeamMembersList
          members={members}
          roles={teamRoles}
          onInviteMember={handleInviteDialogOpen}
          onUpdateMemberRole={updateMemberRole}
          onRemoveMember={removeMember}
        />
      </TabPanel>

      {/* Invitations tab */}
      <TabPanel value={tabIndex} index={2}>
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2">
              Team Invitations
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refreshInvitations()}
                disabled={invitationsLoading}
              >
                Refresh
              </Button>
              {canInviteMembers && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleInviteDialogOpen}
                >
                  Send New Invitation
                </Button>
              )}
            </Box>
          </Box>

          {/* DataGrid */}
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={transformedInvitations}
              columns={invitationColumns}
              loading={invitationsLoading}
              getRowId={(row) => row.id}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: {
                  sortModel: [{ field: 'createdAt', sort: 'desc' }],
                },
              }}
              localeText={{
                noRowsLabel: 'No invitations sent yet',
                noResultsOverlayLabel: 'No invitations found',
                MuiTablePagination: {
                  labelRowsPerPage: 'Invitations per page',
                },
              }}
              sx={{
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'background.default',
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-virtualScroller': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>
        </Box>
      </TabPanel>

      {/* Roles tab */}
      <TabPanel value={tabIndex} index={3}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Team Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage custom roles and permissions for your team members.
          </Typography>
        </Box>

        <RoleManager teamId={teamId || ''} level="team" />
      </TabPanel>

      {/* Settings tab */}
      <TabPanel value={tabIndex} index={4}>
        {team && (
          <Box>
            {/* Team Details Section */}
            <TeamSettings
              team={{ ...team, id: team._id || (team as any).id || '' } as any}
              onUpdateTeam={updateTeam}
              organizationId={orgId}
            />

            {/* Team Actions Section */}
            <Card
              sx={{
                mt: 3,
                borderRadius: 2,
                borderColor: 'divider',
                boxShadow: 'none',
                '&:hover': { transform: 'none', boxShadow: 'none' }
              }}
            >
              <CardHeader
                avatar={<StarIcon color="warning" />}
                title="Team Actions"
                subheader="Manage your membership and team operations"
              />
              <Divider />
              <CardContent sx={{ pt: 2 }}>
                <Stack spacing={1.5}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<LeaveIcon />}
                    onClick={() => setLeaveTeamDialogOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Leave Team
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Danger Zone - Only show if user can delete team */}
            {canDeleteTeam() && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'error.main' }}>
                  Danger Zone
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'error.light',
                    bgcolor: 'error.lighter'
                  }}
                >
                  {/* Delete Team */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Delete this team
                      </Typography>
                      <Typography variant="body2">
                        Once you delete a team, there is no going back. Please be certain.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        if (deleteTeam) {
                          deleteTeam();
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Invite Member Dialog */}
      <BulkInviteDialog
        open={inviteDialogOpen}
        onClose={handleInviteDialogClose}
        onComplete={handleBulkInviteComplete}
        organizationId={orgId || ''}
        teamId={team?._id || (team as any)?.id || ''}
        teamName={team?.name || ''}
      />
      
      {/* Subscription Dialog */}
      <TeamSubscriptionDialog
        open={changeSubscriptionDialogOpen}
        onClose={handleSubscriptionDialogClose}
        availableSubscriptions={availableSubscriptions}
        currentSubscription={teamSubscription}
        orgId={orgId || ''}
        onChangeSubscription={handleChangeSubscription}
      />

      {/* Context Menu for Invitations */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCancelInvitation}>
          <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Invitation</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* Leave Team Confirmation Dialog */}
      <ConfirmDialog
        open={leaveTeamDialogOpen}
        title="Leave Team"
        content="Are you sure you want to leave this team? You will lose access to all team resources and content. This action cannot be undone."
        confirmText="Leave Team"
        cancelText="Cancel"
        onConfirm={handleLeaveTeam}
        onCancel={() => setLeaveTeamDialogOpen(false)}
        severity="warning"
        isLoading={isLeavingTeam}
      />
    </Container>
  );
};

export default TeamDetail;
